"use client"
import React, {useRef} from 'react'

type Segment = 'hours' | 'minutes' | 'seconds';

interface DurationInputProps {
    value: number; // total seconds
    onChange: (seconds: number) => void;
    ariaLabel: string;
    maxSeconds?: number;
}

function splitSeconds(total: number) {
    const safe = Math.max(0, Math.floor(total));
    return {
        hours: Math.floor(safe / 3600),
        minutes: Math.floor((safe % 3600) / 60),
        seconds: safe % 60,
    };
}

function pad(value: number, length: number) {
    return String(value).padStart(length, '0');
}

export function DurationInput({value, onChange, ariaLabel, maxSeconds}: DurationInputProps) {
    const parts = splitSeconds(value);
    const typingRef = useRef<{segment: Segment | null; buffer: string}>({segment: null, buffer: ''});

    function emit(next: {hours: number; minutes: number; seconds: number}) {
        let total = next.hours * 3600 + next.minutes * 60 + next.seconds;
        total = Math.max(0, total);
        if (maxSeconds != null) total = Math.min(maxSeconds, total);
        onChange(total);
    }

    function changeSegment(segment: Segment, raw: number) {
        const current = splitSeconds(value);
        let segValue = raw;
        if (segment === 'minutes' || segment === 'seconds') {
            segValue = ((segValue % 60) + 60) % 60;
        } else {
            segValue = Math.max(0, segValue);
        }
        emit({...current, [segment]: segValue});
    }

    function resetTyping() {
        typingRef.current = {segment: null, buffer: ''};
    }

    // Shared digit handling used by both physical keyboards (keydown) and
    // mobile virtual keyboards (beforeinput).
    function typeDigit(segment: Segment, digit: string) {
        const state = typingRef.current;
        const buffer = state.segment === segment ? state.buffer + digit : digit;
        const numeric = parseInt(buffer, 10);

        const maxDigits = segment === 'hours' ? buffer.length : 2;
        const trimmed = buffer.slice(-maxDigits);

        changeSegment(segment, parseInt(trimmed, 10));

        // For mm/ss, once two digits typed (or value forces overflow) reset buffer.
        const reachedLimit = segment !== 'hours' && (buffer.length >= 2 || numeric * 10 > 59);
        typingRef.current = reachedLimit
            ? {segment: null, buffer: ''}
            : {segment, buffer: trimmed};
    }

    function handleKeyDown(segment: Segment, e: React.KeyboardEvent<HTMLInputElement>) {
        const current = splitSeconds(value)[segment];

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            resetTyping();
            changeSegment(segment, current + 1);
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            resetTyping();
            changeSegment(segment, current - 1);
            return;
        }
        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();
            typeDigit(segment, e.key);
            return;
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            resetTyping();
            changeSegment(segment, 0);
            return;
        }
    }

    // Mobile virtual keyboards (notably Android) don't emit usable `keydown`
    // events, so handle editing through `beforeinput` as well. The inputs are
    // fully controlled, so we always prevent the native edit and apply our own.
    function handleBeforeInput(segment: Segment, e: React.FormEvent<HTMLInputElement>) {
        const native = e.nativeEvent as InputEvent;
        e.preventDefault();

        if (native.inputType === 'deleteContentBackward' || native.inputType === 'deleteContentForward') {
            resetTyping();
            changeSegment(segment, 0);
            return;
        }

        const data = native.data ?? '';
        for (const ch of data) {
            if (/[0-9]/.test(ch)) typeDigit(segment, ch);
        }
    }

    const segmentClass = [
        "group px-0.5 my-auto box-content tabular-nums text-start w-[2ch] bg-transparent border-0",
        "inline-block outline-solid outline-transparent rounded-md appearance-none",
        "text-foreground focus:bg-default-200 focus:text-foreground cursor-text select-none",
    ].join(" ");

    const renderSegment = (segment: Segment, text: string, label: string) => (
        <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            role="spinbutton"
            aria-label={label}
            aria-valuenow={splitSeconds(value)[segment]}
            className={segmentClass}
            value={text}
            size={text.length}
            onChange={() => { /* controlled via keydown/beforeinput */ }}
            onKeyDown={(e) => handleKeyDown(segment, e)}
            onBeforeInput={(e) => handleBeforeInput(segment, e)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={resetTyping}
        />
    );

    return (
        <div
            role="group"
            aria-label={ariaLabel}
            className={[
                "relative px-3 gap-3 inline-flex flex-row items-center h-10 min-w-fit rounded-medium",
                "bg-default-100 hover:bg-default-200 transition-colors cursor-text tap-highlight-transparent shadow-xs",
            ].join(" ")}
        >
            <div className="flex items-center text-default-400 gap-x-0 h-6 font-normal tabular-nums">
                {renderSegment('hours', pad(parts.hours, 2), `${ariaLabel} hours`)}
                <span className="px-0.5">:</span>
                {renderSegment('minutes', pad(parts.minutes, 2), `${ariaLabel} minutes`)}
                <span className="px-0.5">:</span>
                {renderSegment('seconds', pad(parts.seconds, 2), `${ariaLabel} seconds`)}
            </div>
        </div>
    );
}
